import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang } from "@/context/LangContext";

const CLUBS = [
    { name: "Eintracht Frankfurt",  logo: "/site_assets/clubs/eintracht-frankfurt.jpg" },
    { name: "Kickers Offenbach",    logo: "/site_assets/clubs/kickers-offenbach.jpg" },
    { name: "Rot-Weiß Frankfurt",   logo: "/site_assets/clubs/rot-weiss-frankfurt.jpg" },
    { name: "TSG Königstein",       logo: "/site_assets/clubs/tsg-konigstein.jpg" },
    { name: "VfB Unterliederbach",  logo: "/site_assets/clubs/vfb-unterliederbach.jpg" },
    { name: "SG Höchst",            logo: "/site_assets/clubs/sg-hochst.jpg" },
    { name: "FSV Frankfurt",        logo: "/site_assets/clubs/fsv-frankfurt.jpg" },
    { name: "TSG Hoffenheim",       logo: "/site_assets/clubs/tsg-hoffenheim.jpg" },
    { name: "DFB",                  logo: "/site_assets/clubs/dfb.jpg" },
];

function ClubBadge({ name, logo }) {
    return (
        <div className="shrink-0 mx-8 flex flex-col items-center gap-2 group">
            <div className="w-24 h-24 flex items-center justify-center">
                <img src={logo} alt={name}
                     className="max-w-full max-h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity" />
            </div>
            <span className="font-heading text-[10px] uppercase tracking-widest text-white/50 whitespace-nowrap">
                {name}
            </span>
        </div>
    );
}

function Carousel({ items, testidSuffix = "" }) {
    const [idx, setIdx] = useState(0);
    const n = items.length;
    const prev = useCallback(() => setIdx((i) => (i - 1 + n) % n), [n]);
    const next = useCallback(() => setIdx((i) => (i + 1) % n), [n]);

    // Reset index when list changes (language switch)
    useEffect(() => { setIdx(0); }, [n]);

    // touch swipe
    const [touchX, setTouchX] = useState(null);
    const onTouchStart = (e) => setTouchX(e.touches[0].clientX);
    const onTouchEnd = (e) => {
        if (touchX == null) return;
        const dx = e.changedTouches[0].clientX - touchX;
        if (dx > 50) prev();
        else if (dx < -50) next();
        setTouchX(null);
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [prev, next]);

    if (n === 0) return (
        <div className="text-center py-16 text-white/40 font-heading tracking-widest">NO REVIEWS AVAILABLE</div>
    );

    const visible = [
        items[(idx - 1 + n) % n],
        items[idx],
        items[(idx + 1) % n],
    ];

    return (
        <div className="relative">
            <div
                className="flex items-stretch justify-center gap-4 md:gap-6 py-8 select-none"
                onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
                data-testid={`reviews-carousel${testidSuffix ? `-${testidSuffix}` : ""}`}
            >
                {visible.map((r, position) => {
                    const isCenter = position === 1;
                    const isProgramReview = r.review_type === "programs";
                    return (
                        <article
                            key={r.id + "-" + position}
                            data-testid={isCenter ? `review-center${testidSuffix ? `-${testidSuffix}` : ""}` : `review-side-${position}${testidSuffix ? `-${testidSuffix}` : ""}`}
                            onClick={() => { if (position === 0) prev(); else if (position === 2) next(); }}
                            className={`transition-all duration-500 ease-out flex-shrink-0 ${
                                isCenter
                                    ? "w-full md:w-[560px] scale-100 opacity-100 z-10"
                                    : "hidden md:block md:w-[380px] scale-90 opacity-40 blur-[2px] cursor-pointer hover:opacity-60"
                            }`}
                        >
                            <div className="h-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-black border border-white/10 p-8 md:p-10 flex flex-col">
                                <Quote className="text-volt" size={28} />
                                {r.program_ref && (
                                    <div className="mt-3 text-volt font-heading text-xs tracking-[0.25em]">
                                        {r.program_ref}
                                    </div>
                                )}
                                {!isProgramReview && r.club_name && (
                                    <div className="mt-3 text-volt font-heading text-xs tracking-[0.25em]">
                                        {r.club_name}
                                    </div>
                                )}
                                <p className={`mt-4 text-white/85 leading-relaxed flex-1 ${
                                    isCenter ? "text-base md:text-lg" : "text-sm"
                                }`}>
                                    "{r.quote}"
                                </p>
                                {!isProgramReview && (
                                    <div className="mt-6 pt-5 border-t border-white/10">
                                        <div className="font-heading text-xl uppercase">{r.student_name}</div>
                                        {r.club_name && (
                                            <div className="text-xs uppercase tracking-widest text-white/50 mt-1">
                                                {r.club_name}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>

            {/* Nav arrows */}
            <div className="flex items-center justify-center gap-3 mt-2">
                <button onClick={prev} data-testid={`carousel-prev${testidSuffix ? `-${testidSuffix}` : ""}`}
                        className="w-10 h-10 border border-white/20 hover:border-volt hover:text-volt flex items-center justify-center transition-colors">
                    <ChevronLeft size={18} />
                </button>
                <div className="text-xs uppercase tracking-widest text-white/50 min-w-[3rem] text-center">
                    {idx + 1} / {n}
                </div>
                <button onClick={next} data-testid={`carousel-next${testidSuffix ? `-${testidSuffix}` : ""}`}
                        className="w-10 h-10 border border-white/20 hover:border-volt hover:text-volt flex items-center justify-center transition-colors">
                    <ChevronRight size={18} />
                </button>
            </div>
        </div>
    );
}


export default function ReviewsPage() {
    const { t } = useLang();
    const location = useLocation();
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (location.hash === "#programs") {
            const el = document.getElementById("programs-reviews");
            if (el) el.scrollIntoView({ behavior: "smooth" });
        }
    }, [location.hash, reviews]);

    const trainings = useMemo(
        () => reviews.filter((r) => r.review_type === "trainings"),
        [reviews]
    );
    const programs = useMemo(
        () => reviews.filter((r) => r.review_type === "programs"),
        [reviews]
    );

    return (
        <div data-testid="reviews-page" className="px-6 md:px-12 lg:px-20 py-16 topo-bg">
            <div className="mb-12 text-center">
                <div className="font-body text-xs uppercase tracking-[0.25em] text-volt mb-3">
                    Elite Player Academy
                </div>
                <h1 className="text-5xl md:text-6xl font-heading uppercase">{t("reviews_title")}</h1>
                <p className="mt-4 text-white/60 max-w-2xl mx-auto">{t("reviews_sub")}</p>
            </div>

            {/* Trainings Reviews */}
            <section id="trainings-reviews" className="mb-20">
                <div className="text-center mb-2">
                    <div className="inline-block font-heading text-volt tracking-[0.3em] text-sm border-b border-volt/40 pb-1">
                        {t("rev_trainings").toUpperCase()}
                    </div>
                </div>
                <Carousel items={trainings} testidSuffix="trainings" />
            </section>

            {/* Programs Reviews */}
            <section id="programs-reviews" className="mb-20">
                <div className="text-center mb-2">
                    <div className="inline-block font-heading text-volt tracking-[0.3em] text-sm border-b border-volt/40 pb-1">
                        {t("rev_programs").toUpperCase()}
                    </div>
                </div>
                <Carousel items={programs} testidSuffix="programs" />
            </section>

            {/* OUR PLAYERS FROM */}
            <section data-testid="clubs-marquee" className="mt-20 border-t border-b border-white/10 py-10">
                <div className="text-center mb-6">
                    <div className="font-heading text-volt tracking-[0.3em] text-sm">
                        {t("our_players_from").toUpperCase()}
                    </div>
                </div>
                <div className="overflow-hidden">
                    <div className="flex animate-marquee whitespace-nowrap" style={{ width: "max-content" }}>
                        {[...CLUBS, ...CLUBS].map((c, i) => (
                            <ClubBadge key={i} name={c.name} logo={c.logo} />
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
